import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import api from "../services/api";
import Header from "./Header";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

export default function SubCategory() {
  const [data, setData] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState("");
  const [categories, setCategories] = useState([]);
  const [subCategoryData, setSubCategoryData] = useState({
    name: "",
    CategoryId: "",
    image: null,
  });

  const checklogin = localStorage.getItem("login");
  const navigate = useNavigate();

  if (!checklogin) {
    navigate("/");
  }

  const getData = async () => {
    try {
      const res = await api.get("/subcategories");
      setData(res.data);
    } catch (err) {
      toast.error("Failed to fetch subcategories");
    }
  };

  const getCategory = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch (err) {
      toast.error("Failed to fetch categories");
    }
  };

  useEffect(() => {
    getData();
    getCategory();
  }, []);

  const handleDelete = (id) => {
    setDeleteDialog(true);
    setDeleteItemId(id);
  };

  const confirmDelete = async (id) => {
    try {
      const res = await api.delete(`/deleteCategory/${id}`);
      toast.success(res.data.message);
      getData();
      setDeleteDialog(false);
    } catch (err) {
      toast.error("Failed to delete subcategory");
      setDeleteDialog(false);
    }
  };

  const handleOpenAdd = () => {
    setSubCategoryData({ name: "", CategoryId: "", image: null });
    setAddDialog(true);
  };

  const handleCloseAdd = () => {
    setAddDialog(false);
    setSubCategoryData({ name: "", CategoryId: "", image: null });
  };

  const handleOpenEdit = (params) => {
    const updateData = {
      id: params.row.id,
      name: params.row.SubCategoryName,
      image: params.row.image,
      CategoryId: params.row.CategoryId || "",
    };
    setSubCategoryData(updateData);
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSubCategoryData({ name: "", CategoryId: "", image: null });
  };

  const handleFileChange = (e) => {
    setSubCategoryData({
      ...subCategoryData,
      image: e.target.files[0],
    });
  };

  const handleAddSubCategory = async () => {
    if (!subCategoryData.name) {
      toast.error("Subcategory name is required");
      return;
    }
    if (!subCategoryData.CategoryId) {
      toast.error("Please select a category");
      return;
    }

    const formData = new FormData();
    formData.append("CategoryId", subCategoryData.CategoryId);
    formData.append("SubCategoryName", subCategoryData.name);
    formData.append("image", subCategoryData.image);

    try {
      const res = await api.post("/subcategories", formData);
      if (res.data) {
        toast.success(res.data.message);
        handleCloseAdd();
        getData();
      }
    } catch (err) {
      toast.error("Failed to add subcategory");
    }
  };

  const handleUpdateSubCategory = async () => {
    if (!subCategoryData.name) {
      toast.error("Subcategory name is required");
      return;
    }

    const formData = new FormData();
    formData.append("SubCategoryName", subCategoryData.name);
    formData.append("CategoryId", subCategoryData.CategoryId);
    if (subCategoryData.image instanceof File) {
      formData.append("image", subCategoryData.image);
    }

    try {
     await api.put(`/updateSubCategory/${subCategoryData.id}`, formData);
      toast.success("Subcategory updated successfully");
      handleCloseEdit();
      getData();
    } catch (err) {
      toast.error("Failed to update subcategory");
    }
  };

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "CategoryName",
      headerName: "Category",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "SubCategoryName",
      headerName: "Sub Category Name",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "image",
      headerName: "Image",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <img
            src={params.value}
            alt="SubCategory"
            style={{
              width: 60,
              height: 60,
              objectFit: "cover",
              borderRadius: 8,
              border: "2px solid #e0e0e0",
            }}
          />
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
          <IconButton
            size="small"
            onClick={() => handleOpenEdit(params)}
            sx={{
              bgcolor: "#e3f2fd",
              "&:hover": { bgcolor: "#bbdefb" },
            }}
          >
            <EditIcon fontSize="small" sx={{ color: "#1976d2" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            sx={{
              bgcolor: "#ffebee",
              "&:hover": { bgcolor: "#ffcdd2" },
            }}
          >
            <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const rows = data?.map((item, index) => ({
    id: item._id,
    image: item.image,
    CategoryName: item.CategoryId?.CategoryName || "N/A",
    CategoryId: item.CategoryId?._id,
    SubCategoryName: item.SubCategoryName,
    serialNo: index + 1,
  }));

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <Header />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { md: "28px" },
          mt: "64px",
          p: 3,
        }}
      >
        <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" color="#1a237e">
                Sub Category Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage product sub categories
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
              sx={{
                bgcolor: "#1976d2",
                "&:hover": { bgcolor: "#1565c0" },
                textTransform: "none",
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
              }}
            >
              Add Sub Category
            </Button>
          </Box>

          {/* Stats Card */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Card sx={{ flex: 1, p: 2, bgcolor: "#e3f2fd", border: "1px solid #90caf9" }}>
              <Typography variant="body2" color="#1565c0">Total Sub Categories</Typography>
              <Typography variant="h4" fontWeight="bold" color="#0d47a1">{data.length}</Typography>
            </Card>
          </Stack>

          {/* DataGrid */}
          <Box sx={{ height: 500, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              rowHeight={80}
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                },
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "#1565c0",
                  borderRadius: "8px 8px 0 0",
                  overflow: "hidden",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 600,
                  color: "#000",
                },
              }}
            />
          </Box>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ bgcolor: "#ffebee", color: "#c62828" }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography>Are you sure you want to delete this sub category? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteDialog(false)} sx={{ color: "#757575" }}>
              Cancel
            </Button>
            <Button
              onClick={() => confirmDelete(deleteItemId)}
              variant="contained"
              color="error"
              sx={{ textTransform: "none" }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Sub Category Dialog */}
        <Dialog open={addDialog} onClose={handleCloseAdd} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">
              Add New Sub Category
            </Typography>
            <IconButton onClick={handleCloseAdd} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={subCategoryData.CategoryId}
                  label="Category"
                  onChange={(e) => setSubCategoryData({ ...subCategoryData, CategoryId: e.target.value })}
                >
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.CategoryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Sub Category Name"
                value={subCategoryData.name}
                onChange={(e) => setSubCategoryData({ ...subCategoryData, name: e.target.value })}
                placeholder="Enter sub category name"
              />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Sub Category Image
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ py: 1.5, textTransform: "none" }}
                >
                  {subCategoryData.image ? subCategoryData.image.name : "Choose Image"}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseAdd} sx={{ color: "#757575" }}>
              Cancel
            </Button>
            <Button
              onClick={handleAddSubCategory}
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              Add Sub Category
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Sub Category Dialog */}
        <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">
              Edit Sub Category
            </Typography>
            <IconButton onClick={handleCloseEdit} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={subCategoryData.CategoryId}
                  label="Category"
                  onChange={(e) => setSubCategoryData({ ...subCategoryData, CategoryId: e.target.value })}
                >
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.CategoryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Sub Category Name"
                value={subCategoryData.name}
                onChange={(e) => setSubCategoryData({ ...subCategoryData, name: e.target.value })}
                placeholder="Enter sub category name"
              />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Sub Category Image
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ py: 1.5, textTransform: "none" }}
                >
                  {subCategoryData.image instanceof File ? subCategoryData.image.name : "Choose New Image"}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseEdit} sx={{ color: "#757575" }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSubCategory}
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              Update Sub Category
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
